'use client'

import {
  selectCustomisation,
  updateCustomisationSetting,
} from '@/redux/customisation'
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation'
import { ReactNode, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectDetailMenuSite } from 'src/redux/meta'
import { isObjectEmpty } from 'src/utils/object'
import { getCurrentPage, makeQueryString } from 'src/utils/routes'
import { isEmpty } from 'src/utils/string'

const getPageOptions = ({
  query,
  sectionSettingData,
}: {
  query: {
    path: string
    locale: string
    previewToken: string | null | undefined
    previewMode: string | null | undefined
  }
  sectionSettingData: Customisation.SectionSettingData
}) => {
  const currentPage = getCurrentPage(query)
  return sectionSettingData.pages[currentPage]?.options || {}
}

const FrameConnector = ({
  children,
  previewMode,
}: {
  children: ReactNode
  previewMode: boolean
}): ReactNode => {
  const router = useRouter()
  const params = useParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const dispatch = useDispatch()
  const { currentAction, sectionSettingData } = useSelector(selectCustomisation)
  const metaState = useSelector(selectDetailMenuSite)
  const { enableNewCheckoutFlow } = metaState?.features || {}

  const sendMessageToParentFrame = (message: {
    type: string
    payload: {
      url: {
        path: string
        locale: string
        previewToken: string | null | undefined
        previewMode: string | null | undefined
      }
    }
  }): void => {
    if (previewMode) {
      window.parent.postMessage(
        message,
        process.env.NEXT_PUBLIC_CUSTOMISATION_BASE_URL as string
      )
    }
  }

  const dispatchActionFromParentFrame = (event: MessageEvent): void => {
    // Chrome browser on iOS version 12 will send an event on every user interaction on behalf
    // of the parent origin https://stackoverflow.com/questions/54079756/internal-chromium-postmessage-event
    // (no official documentation found yet).

    // Therefore, to prevent Oddle Shop to rerender with an unnecessary data which leads to a crash,
    // only dispatch the action if the event is fired from Oddle Shop Editor
    // which is distinguished by the non-empty type field in the currentAction object
    if (
      event.origin === process.env.NEXT_PUBLIC_CUSTOMISATION_BASE_URL &&
      !isObjectEmpty(event.data.currentAction) &&
      !isEmpty(event.data.currentAction.type)
    ) {
      dispatch(updateCustomisationSetting(event.data))
    }
  }

  useEffect(() => {
    const { canNavigateFromCustomisationUI } = getPageOptions({
      query: {
        path: (pathname as string)
          .replace(params?.locale as string, '')
          .replaceAll('/', ''),
        locale: params?.locale as string,
        previewToken: searchParams?.get('previewToken'),
        previewMode: searchParams?.get('previewMode'),
      },
      sectionSettingData,
    })
    if (canNavigateFromCustomisationUI === false) return
    sendMessageToParentFrame({
      type: '@@preview/PREVIEW_FRAME_ROUTE_CHANGE_START',
      payload: {
        url: {
          path: (pathname as string)
            .replace(params?.locale as string, '')
            .replaceAll('/', ''),
          locale: params?.locale as string,
          previewToken: searchParams?.get('previewToken'),
          previewMode: searchParams?.get('previewMode'),
        },
      },
    })
  }, [pathname])

  useEffect(() => {
    if (previewMode) {
      window.addEventListener('message', dispatchActionFromParentFrame)
      return () => {
        window.removeEventListener('message', dispatchActionFromParentFrame)
      }
    }
  }, [])

  const didMountRef = useRef(currentAction)

  useEffect(() => {
    if (
      didMountRef.current !== currentAction &&
      currentAction.type === '@@preview/CHANGE_PAGE'
    ) {
      didMountRef.current = currentAction
      switch (currentAction.payload.pageId) {
        case 'product':
          router.push(
            `/${params?.locale}${makeQueryString({
              productId: 'preview',
              previewMode: 'true',
              previewToken: currentAction.payload.previewToken,
            })}`
          )
          break
        case 'checkout':
          router.push(
            `/${params?.locale}/${
              currentAction.payload.pageId
            }/preview${makeQueryString({
              previewMode: 'true',
              previewToken: currentAction.payload.previewToken,
              ...(enableNewCheckoutFlow
                ? {
                    step_address: 'true',
                  }
                : {}),
            })}`
          )
          break
        case 'invoice':
          router.push(
            `/${params?.locale}/${
              currentAction.payload.pageId
            }/preview${makeQueryString({
              previewMode: 'true',
              previewToken: currentAction.payload.previewToken,
            })}`
          )
          break
        case 'index':
          router.push(
            `/${params?.locale}${makeQueryString({
              previewMode: 'true',
              previewToken: currentAction.payload.previewToken,
            })}`
          )
          break
        default:
          router.push(
            `/${params?.locale}/${
              currentAction.payload.pageId
            }${makeQueryString({
              previewMode: 'true',
              previewToken: currentAction.payload.previewToken,
            })}`
          )
          break
      }
    }
  }, [currentAction, enableNewCheckoutFlow, params, router])

  return children
}

export default FrameConnector
